"use client";

import { type UseContactSalesFormReturn } from "../hooks/useContactSalesForm";
import { isPersonalEmail, buildFieldOptions, isExpectedMonthlyUsageCountry } from "../shared/utils";
import { companySizeOptions, fieldConfig } from "../shared/constants";

type Step2Props = Pick<UseContactSalesFormReturn, "form" | "validators" | "t"> & {
  showTestVariant?: boolean;
};

export function Step2({ form, validators, t, showTestVariant }: Step2Props) {
  return (
    <>
      <div className='tw-flex tw-flex-col @sm:tw-flex-row @sm:tw-gap-4'>
        <div className='tw-flex-1 tw-min-w-0'>
          <form.AppField
            name={fieldConfig.first_name.name}
            validators={{
              onMount: validators.first_name,
              onChange: validators.first_name,
            }}
          >
            {field => (
              <field.Input
                name={fieldConfig.first_name.name}
                label={t("fields.first_name.label")}
                placeholder={showTestVariant ? undefined : t("fields.first_name.placeholder")}
                required={fieldConfig.first_name.required}
                showTestVariant={showTestVariant}
              />
            )}
          </form.AppField>
        </div>
        <div className='tw-flex-1 tw-min-w-0'>
          <form.AppField
            name={fieldConfig.last_name.name}
            validators={{
              onMount: validators.last_name,
              onChange: validators.last_name,
            }}
          >
            {field => (
              <field.Input
                name={fieldConfig.last_name.name}
                label={t("fields.last_name.label")}
                placeholder={showTestVariant ? undefined : t("fields.last_name.placeholder")}
                required={fieldConfig.last_name.required}
                showTestVariant={showTestVariant}
              />
            )}
          </form.AppField>
        </div>
      </div>
      <form.Subscribe selector={state => state.values.email}>
        {email =>
          isPersonalEmail(email) && (
            <>
              <form.AppField
                name={fieldConfig.company.name}
                validators={{
                  onMount: validators.company,
                  onChange: validators.company,
                }}
              >
                {field => (
                  <field.Input
                    name={fieldConfig.company.name}
                    label={t("fields.company.label")}
                    placeholder={showTestVariant ? undefined : t("fields.company.placeholder")}
                    required={fieldConfig.company.required}
                    showTestVariant={showTestVariant}
                  />
                )}
              </form.AppField>
              <div className='tw-flex tw-flex-col @md:tw-flex-row @md:tw-gap-4'>
                <div className='tw-flex-1 @md:tw-min-w-0'>
                  <form.AppField
                    name={fieldConfig.industry.name}
                    validators={{
                      onMount: validators.industry,
                      onChange: validators.industry,
                    }}
                  >
                    {field => (
                      <field.Select
                        name={fieldConfig.industry.name}
                        label={t("fields.industry.label")}
                        unselectedLabel={t("fields.industry.unselectedLabel")}
                        options={buildFieldOptions("industry", t)}
                        required={fieldConfig.industry.required}
                        showTestVariant={showTestVariant}
                      />
                    )}
                  </form.AppField>
                </div>
                <div className='tw-flex-1 @md:tw-min-w-0'>
                  <form.AppField
                    name={fieldConfig.company_size.name}
                    validators={{
                      onMount: validators.company_size,
                      onChange: validators.company_size,
                    }}
                  >
                    {field => (
                      <field.Select
                        name={fieldConfig.company_size.name}
                        label={t("fields.company_size.label")}
                        unselectedLabel={t("fields.company_size.unselectedLabel")}
                        options={companySizeOptions}
                        required={fieldConfig.company_size.required}
                        showTestVariant={showTestVariant}
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            </>
          )
        }
      </form.Subscribe>
      <div className='tw-flex @lg:tw-gap-4 @lg:tw-flex-row tw-flex-col'>
        <div className='tw-flex-1 @lg:tw-min-w-0'>
          <form.AppField
            name={fieldConfig.country.name}
            validators={{
              onMount: validators.country,
              onChange: validators.country,
            }}
          >
            {field => (
              <field.CountrySelect
                name={fieldConfig.country.name}
                label={t("fields.country.label")}
                required={fieldConfig.country.required}
                showTestVariant={showTestVariant}
              />
            )}
          </form.AppField>
        </div>
        <div className='tw-flex-1 @lg:tw-min-w-0'>
          <form.Subscribe selector={state => state.values.country}>
            {country => (
              <form.AppField
                name={fieldConfig.phone_number.name}
                validators={{
                  onMount: validators.phone_number,
                  onChange: validators.phone_number,
                }}
              >
                {field => (
                  <field.PhoneInput
                    name={fieldConfig.phone_number.name}
                    label={t("fields.phone_number.label")}
                    placeholder='123 456 7890'
                    required={fieldConfig.phone_number.required}
                    country={country}
                    showTestVariant={showTestVariant}
                  />
                )}
              </form.AppField>
            )}
          </form.Subscribe>
        </div>
      </div>
      {showTestVariant && (
        <>
          <div className='tw-flex tw-flex-col @md:tw-flex-row @md:tw-gap-4'>
            <div className='tw-flex-1 tw-min-w-0'>
              <form.AppField name={fieldConfig.platform_interest.name}>
                {field => (
                  <field.Select
                    name={fieldConfig.platform_interest.name}
                    label={t("fields.platform_interest.label_test_variant")}
                    unselectedLabel={t("fields.platform_interest.unselectedLabel")}
                    options={buildFieldOptions("platform_interest", t)}
                    required={fieldConfig.platform_interest.required}
                    showTestVariant={showTestVariant}
                  />
                )}
              </form.AppField>
            </div>
            <div className='tw-flex-1 tw-min-w-0'>
              <form.AppField
                name={fieldConfig.how_did_you_hear.name}
                validators={{
                  onMount: validators.how_did_you_hear,
                  onChange: validators.how_did_you_hear,
                }}
              >
                {field => (
                  <field.Select
                    name={fieldConfig.how_did_you_hear.name}
                    label={t("fields.how_did_you_hear.label")}
                    unselectedLabel={t("fields.how_did_you_hear.unselectedLabel")}
                    options={buildFieldOptions("how_did_you_hear", t)}
                    required={fieldConfig.how_did_you_hear.required}
                    showTestVariant={showTestVariant}
                  />
                )}
              </form.AppField>
            </div>
          </div>
          <form.Subscribe selector={state => state.values.how_did_you_hear}>
            {howDidYouHear =>
              howDidYouHear === "Other" && (
                <form.AppField
                  name={fieldConfig.how_did_you_hear_other.name}
                  validators={{
                    onMount: validators.how_did_you_hear_other,
                    onChange: validators.how_did_you_hear_other,
                  }}
                >
                  {field => (
                    <field.Input
                      name={fieldConfig.how_did_you_hear_other.name}
                      label={t("fields.how_did_you_hear_other.label")}
                      placeholder={t("fields.how_did_you_hear_other.placeholder")}
                      required={fieldConfig.how_did_you_hear_other.required}
                      showTestVariant={showTestVariant}
                    />
                  )}
                </form.AppField>
              )
            }
          </form.Subscribe>
        </>
      )}
      <form.Subscribe selector={state => state.values.country}>
        {country =>
          isExpectedMonthlyUsageCountry(country) && (
            <form.AppField
              name={fieldConfig.expected_monthly_usage.name}
              validators={{
                onChangeListenTo: ["country"],
                onMount: validators.expected_monthly_usage,
                onChange: validators.expected_monthly_usage,
              }}
            >
              {field => (
                <field.Select
                  name={fieldConfig.expected_monthly_usage.name}
                  label={t("fields.expected_monthly_usage.label")}
                  unselectedLabel={t("fields.expected_monthly_usage.unselectedLabel")}
                  options={buildFieldOptions("expected_monthly_usage", t)}
                  required={fieldConfig.expected_monthly_usage.required}
                  showTestVariant={showTestVariant}
                />
              )}
            </form.AppField>
          )
        }
      </form.Subscribe>
      {!showTestVariant && (
        <form.AppField name={fieldConfig.platform_interest.name}>
          {field => (
            <field.Select
              name={fieldConfig.platform_interest.name}
              label={t("fields.platform_interest.label")}
              unselectedLabel={t("fields.platform_interest.unselectedLabel")}
              options={buildFieldOptions("platform_interest", t)}
              required={fieldConfig.platform_interest.required}
              showTestVariant={showTestVariant}
            />
          )}
        </form.AppField>
      )}
      <form.AppField
        name={fieldConfig.detail.name}
        validators={{
          onMount: validators.detail,
          onChange: validators.detail,
        }}
      >
        {field => (
          <field.Textarea
            name={fieldConfig.detail.name}
            label={showTestVariant ? t("fields.detail.label_test_variant") : t("fields.detail.label")}
            placeholder={showTestVariant ? t("fields.detail.placeholder_test_variant") : t("fields.detail.placeholder")}
            required={fieldConfig.detail.required}
            showTestVariant={showTestVariant}
          />
        )}
      </form.AppField>
      {!showTestVariant && (
        <form.AppField
          name={fieldConfig.how_did_you_hear.name}
          validators={{
            onMount: validators.how_did_you_hear,
            onChange: validators.how_did_you_hear,
          }}
        >
          {field => (
            <field.Input
              name={fieldConfig.how_did_you_hear.name}
              label={t("fields.how_did_you_hear.label")}
              placeholder={t("fields.how_did_you_hear.placeholder")}
              required={fieldConfig.how_did_you_hear.required}
              showTestVariant={showTestVariant}
            />
          )}
        </form.AppField>
      )}
    </>
  );
}
